import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useUpload } from '../contexts/UploadContext';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Avatar,
  alpha,
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import PageAlert from './PageAlert';

import { PLATFORM_CONFIG, PLATFORM_LIST } from './post/PLATFORM_CONFIG';
import { useFeatureToggles } from '../contexts/FeatureToggleContext';
import { FEATURES } from '../constants/features';
import PlatformCustomizationPanel from './post/PlatformCustomizationPanel';
import PostingProgressModal from './post/PostingProgressModal';
import SuggestionPanel from './post/SuggestionPanel';
import { analyzeApi } from '../services/api';
import ConnectPlatformsCTA from './ConnectPlatformsCTA';

import dayjs from 'dayjs';


// ─── Validation (no shared content — each platform is self-contained) ────────

function validateForm(selectedPlatforms, platformCustomizations) {
  const errors = {};

  if (selectedPlatforms.length === 0) {
    errors.platforms = 'Select at least one platform';
    return errors;
  }

  selectedPlatforms.forEach((platform) => {
    const c = platformCustomizations[platform] || {};
    const config = PLATFORM_CONFIG[platform];
    const hasMedia = !!c.media_file;
    const isScheduled = !!c.isScheduled;

    // Strict Media Requirement
    if (config?.isMediaRequired && !hasMedia) {
      errors[`${platform}_media`] = `Media (image/video) is required for ${config.label}`;
    }

    switch (platform) {
      case 'youtube':
        if (!c.title?.trim()) errors.youtube_title = 'Title is required for YouTube';
        break;
      case 'twitter':
        if (!c.text?.trim() && !hasMedia) errors.twitter_text = 'Tweet text or media is required';
        break;
      case 'reddit':
        if (!c.subreddit?.trim()) errors.reddit_subreddit = 'Subreddit is required';
        if (!c.title?.trim()) errors.reddit_title = 'Title is required for Reddit';
        if (isScheduled) errors.reddit_scheduled = 'Reddit scheduling is coming soon';
        break;
      case 'instagram':
        if (!c.text?.trim() && !hasMedia) errors.instagram_text = 'Caption or media is required';
        break;
      case 'facebook':
        if (!c.text?.trim() && !hasMedia) errors.facebook_text = 'Post text or media is required';
        if (isScheduled) errors.facebook_scheduled = 'Facebook scheduling is coming soon';
        break;
      case 'linkedin':
        if (!c.text?.trim() && !hasMedia) errors.linkedin_text = 'Post text or media is required';
        if (isScheduled) errors.linkedin_scheduled = 'LinkedIn does not support scheduling yet. Please toggle "Post Now".';
        break;
      default:
        break;
    }
  });

  return errors;
}

// ─── Platform header chip ─────────────────────────────────────────────────────

function PlatformChip({ platform, selected, onClick }) {
  const config = PLATFORM_CONFIG[platform.value];
  return (
    <Box
      onClick={onClick}
      data-testid={`platform-chip-${platform.value}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2.5,
        py: 1,
        borderRadius: 4,
        cursor: 'pointer',
        fontWeight: 800,
        fontSize: '0.85rem',
        color: selected ? 'white' : 'text.primary',
        userSelect: 'none',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        background: selected
          ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
          : alpha('#64748b', 0.05),
        border: '1px solid',
        borderColor: selected ? 'transparent' : alpha('#64748b', 0.1),
        boxShadow: selected ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none',
        '&:hover': {
          background: selected
            ? 'linear-gradient(135deg, #4f46e5, #7c3aed)'
            : alpha('#64748b', 0.1),
          transform: 'translateY(-1px)',
        },
      }}
    >
      {React.cloneElement(platform.icon, { sx: { fontSize: 18 } })}
      <span>{config.label}</span>
      {selected && <CheckCircleIcon sx={{ fontSize: 16, ml: 0.5 }} />}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const PostContent = React.memo(({ onPost, error }) => {
  // Unified error extraction
  const errorMsg = typeof error === 'string' ? error : (error?.message || '');
  const isNoAccountsError = !!errorMsg.toLowerCase().match(/no .*accounts|link.*platforms|social.*media.*setup/i);

  const location = useLocation();
  const initialScheduledDate = location.state?.scheduledDate;
  const initialPlatforms = location.state?.initialPlatforms;

  // Filter available platforms by feature toggle allowed_platforms
  const { getAllowedPlatforms } = useFeatureToggles();
  const allowedPlatforms = getAllowedPlatforms(FEATURES.POSTING);
  const filteredPlatformList = PLATFORM_LIST.filter(p => allowedPlatforms.includes(p.value));

  // Default to first allowed platform; filter out any pre-selected platforms that are now disallowed
  const defaultPlatform = allowedPlatforms[0] || 'youtube';
  const [selectedPlatforms, setSelectedPlatforms] = useState(
    initialPlatforms ? initialPlatforms.filter(p => allowedPlatforms.includes(p)) : [defaultPlatform]
  );
  const [expandedPlatform, setExpandedPlatform] = useState(
    initialPlatforms?.[0] && allowedPlatforms.includes(initialPlatforms[0]) ? initialPlatforms[0] : defaultPlatform
  );
  const [platformCustomizations, setPlatformCustomizations] = useState({});
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Posting modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [postingPlatforms, setPostingPlatforms] = useState([]);
  const [postingStatus, setPostingStatus] = useState({});
  const [postingResults, setPostingResults] = useState({});
  const [retryingPlatform, setRetryingPlatform] = useState(null);

  const { uploadFile } = useUpload();

  // AI Suggestions state
  const [suggestionPanelOpen, setSuggestionPanelOpen] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionContext, setSuggestionContext] = useState(null); // { platform, field }
  const [aiError, setAiError] = useState(null);


  // ── Handlers ──────────────────────────────────────────────────────────────

  const handlePlatformToggle = useCallback((platformValue) => {
    setSelectedPlatforms((prev) => {
      const isSelected = prev.includes(platformValue);
      if (isSelected) {
        // Deselect: close this accordion, leave others as-is
        setExpandedPlatform((cur) => (cur === platformValue ? null : cur));
        return prev.filter((p) => p !== platformValue);
      } else {
        // Select: expand this platform, close the previous one
        setExpandedPlatform(platformValue);
        return [...prev, platformValue];
      }
    });
  }, []);

  const handleCustomizationChange = useCallback((platform, field, value) => {
    setPlatformCustomizations((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value },
    }));
    if (hasSubmitted) {
      setErrors((prev) => ({ ...prev, [`${platform}_${field}`]: undefined }));
    }
  }, [hasSubmitted]);

  // Handle pre-filled scheduled date from Calendar
  useEffect(() => {
    if (initialScheduledDate) {
      const date = dayjs(initialScheduledDate);
      if (date.isValid()) {
        const update = {};
        selectedPlatforms.forEach(p => {
          update[p] = {
            ...(platformCustomizations[p] || {}),
            isScheduled: true,
            scheduledDate: date
          };
        });
        setPlatformCustomizations(prev => ({ ...prev, ...update }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialScheduledDate]); // Only run when initialScheduledDate changes (on mount usually)

  const handleAskAI = useCallback(async (platform) => {
    const customization = platformCustomizations[platform] || {};

    // Pre-flight validation: Check if there's any content to optimize
    const hasContent =
      customization.text?.trim() ||
      customization.title?.trim() ||
      customization.description?.trim() ||
      (Array.isArray(customization.tags) && customization.tags.length > 0) ||
      (Array.isArray(customization.hashtags) && customization.hashtags.length > 0);

    if (!hasContent) {
      setAiError(`Please provide some content (text, title, or tags) for ${platform} first so the AI has something to optimize!`);
      // Auto-clear error after 5 seconds
      setTimeout(() => setAiError(null), 5000);
      return;
    }

    setAiError(null);
    setSuggestionContext({ platform });
    setSuggestionPanelOpen(true);
    setSuggestionLoading(true);
    setSuggestions([]);

    try {
      const data = await analyzeApi.optimizeContent({
        platform,
        content_type: customization.content_type || 'post',
        text: customization.text || '',
        title: customization.title || '',
        tags: customization.tagsRaw || '',
        description: customization.description || '',
      });
      if (data.suggestions) {
        setSuggestions(data.suggestions);
      } else if (data.raw_result) {
        // Fallback for non-JSON suggestions if AI fails to format
        setSuggestions([{ text: data.raw_result, hook: 'General AI Advice' }]);
      }
    } catch (err) {
      console.error('AI Suggestion Error:', err);
      const msg = err?.isQuotaError
        ? 'AI quota reached. Please upgrade your plan.'
        : (err?.message || 'AI optimization failed. Please try again.');
      setAiError(msg);
      setTimeout(() => setAiError(null), 6000);
      setSuggestionPanelOpen(false);
    } finally {
      setSuggestionLoading(false);
    }
  }, [platformCustomizations]);

  const handleApplySuggestion = useCallback((suggestion) => {
    if (!suggestionContext) return;
    const { platform } = suggestionContext;

    if (suggestion.title) handleCustomizationChange(platform, 'title', suggestion.title);
    if (suggestion.text) {
      if (platform === 'youtube') {
        handleCustomizationChange(platform, 'description', suggestion.text);
      } else {
        handleCustomizationChange(platform, 'text', suggestion.text);
      }
    }
    if (suggestion.hashtags) {
      if (platform === 'youtube') {
        const tagsArr = Array.isArray(suggestion.hashtags) ? suggestion.hashtags : suggestion.hashtags.split(',').map(t => t.trim());
        handleCustomizationChange(platform, 'tags', tagsArr);
      } else {
        const hashArr = Array.isArray(suggestion.hashtags) ? suggestion.hashtags : suggestion.hashtags.split(',').map(t => t.trim());
        const formattedHashes = hashArr.map(h => (h.startsWith('#') ? h : `#${h}`));
        handleCustomizationChange(platform, 'hashtags', formattedHashes);
      }
    }

    setSuggestionPanelOpen(false);
  }, [suggestionContext, handleCustomizationChange]);

  const buildPosts = useCallback(
    (platformList = selectedPlatforms) =>
      platformList.map((platform) => {
        const c = platformCustomizations[platform] || {};

        // Append hashtags to the text payload for non-YouTube platforms
        let finalContentText = c.text || '';
        if (platform !== 'youtube' && Array.isArray(c.hashtags) && c.hashtags.length > 0) {
          finalContentText = finalContentText ? `${finalContentText}\n\n${c.hashtags.join(' ')}` : c.hashtags.join(' ');
        }

        return {
          platform,
          content: {
            ...c,
            text: finalContentText, // Use the merged text + hashtags
            media_file: undefined, // files sent separately
            ...(c.isScheduled && c.scheduledDate ? { scheduled_for: c.scheduledDate.toISOString() } : {})
          },
        };
      }),
    [selectedPlatforms, platformCustomizations]
  );

  const buildMediaFiles = useCallback(
    (platformList = selectedPlatforms) => {
      const files = {};
      platformList.forEach((platform) => {
        const c = platformCustomizations[platform] || {};
        if (c.media_file) files[platform] = c.media_file;
        // YouTube thumbnail sent as a separate key
        if (platform === 'youtube' && c.thumbnail_file) {
          files['thumbnail_youtube'] = c.thumbnail_file;
        }
      });
      return files;
    },
    [selectedPlatforms, platformCustomizations]
  );

  const runPosting = useCallback(
    async (platformList, posts, mediaFiles) => {
      try {
        const results = await onPost(posts, mediaFiles);
        const newStatus = {};
        const newResults = {};
        for (const result of results || []) {
          newStatus[result.platform] = result.status === 'success' ? 'success' : 'error';
          newResults[result.platform] = result;
        }
        setPostingStatus((prev) => ({ ...prev, ...newStatus }));
        setPostingResults((prev) => ({ ...prev, ...newResults }));
        return results;
      } catch {
        const errorStatus = {};
        platformList.forEach((p) => { errorStatus[p] = 'error'; });
        setPostingStatus((prev) => ({ ...prev, ...errorStatus }));
        return [];
      }
    },
    [onPost]
  );

  const handleSubmit = useCallback(async () => {
    setHasSubmitted(true);
    const newErrors = validateForm(selectedPlatforms, platformCustomizations);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Auto-expand the first platform that has an error
      const platformWithError = selectedPlatforms.find((p) =>
        Object.keys(newErrors).some((key) => key.startsWith(`${p}_`))
      );
      if (platformWithError) setExpandedPlatform(platformWithError);
      return;
    }

    const platforms = [...selectedPlatforms];
    const posts = buildPosts(platforms);
    const mediaFiles = buildMediaFiles(platforms);

    const initialStatus = Object.fromEntries(platforms.map((p) => [p, 'queued']));
    setPostingPlatforms(platforms);
    setPostingStatus(initialStatus);
    setPostingResults({});
    setModalOpen(true);

    platforms.forEach((platform, i) => {
      setTimeout(() => {
        setPostingStatus((prev) => ({ ...prev, [platform]: 'uploading' }));
      }, i * 200);
    });

    // ─── Phase 1: Cloud Staging (for all media) ───
    const stagingPromises = [];
    const mediaIds = {};

    platforms.forEach((platform) => {
      const c = platformCustomizations[platform] || {};
      if (c.media_file) {
        setPostingStatus(prev => ({ ...prev, [platform]: 'staging_cloud' }));

        const stagingPromise = new Promise((resolve, reject) => {
          uploadFile(c.media_file, (url, mediaId) => {
            if (mediaId) {
              mediaIds[platform] = mediaId;
              resolve();
            } else {
              reject(new Error(`Cloud staging failed for ${platform}`));
            }
          });
        });
        stagingPromises.push(stagingPromise);
      }
    });

    try {
      await Promise.all(stagingPromises);
    } catch (e) {
      console.error('One or more cloud staging tasks failed:', e);
      // Mark all platforms that didn't stage as errored and abort
      platforms.forEach((p) => {
        if (platformCustomizations[p]?.media_file && !mediaIds[p]) {
          setPostingStatus((prev) => ({ ...prev, [p]: 'error' }));
          setPostingResults((prev) => ({
            ...prev,
            [p]: { platform: p, status: 'error', error: 'Media upload failed. Please try again.' },
          }));
        }
      });
      return;
    }

    // ─── Phase 2: Final Posting ───
    const finalPosts = posts.map(p => {
      if (mediaIds[p.platform]) {
        return { ...p, content: { ...p.content, mediaId: mediaIds[p.platform] } };
      }
      return p;
    });

    await runPosting(platforms, finalPosts, mediaFiles);
  }, [selectedPlatforms, platformCustomizations, buildPosts, buildMediaFiles, runPosting, uploadFile]);

  const handleRetry = useCallback(
    async (platform, content) => {
      setRetryingPlatform(platform);
      setPostingStatus((prev) => ({ ...prev, [platform]: 'uploading' }));
      const posts = [{ platform, content }];
      const mediaFiles = buildMediaFiles([platform]);
      await runPosting([platform], posts, mediaFiles);
      setRetryingPlatform(null);
    },
    [buildMediaFiles, runPosting]
  );

  const allDone =
    postingPlatforms.length > 0 &&
    postingPlatforms.every((p) => ['success', 'error'].includes(postingStatus[p]));

  const platformsWithErrors = selectedPlatforms.filter((p) =>
    Object.keys(errors).some((key) => key.startsWith(`${p}_`))
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  // Priority Early Return: If accounts aren't connected, show the "Locked" onboarding state
  if (isNoAccountsError) {
    return (
      <Box sx={{ py: 6 }}>
        <ConnectPlatformsCTA
          title="Posting Hub Locked"
          description="Connect your social media platforms in Settings to broadcast your story across your entire network from one dashboard."
          buttonText="Link Your Platforms"
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1.5 }}>
            Multi-Platform Posting
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Broadcast your story across your entire social network from one place
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {filteredPlatformList.map((platform) => (
            <PlatformChip
              key={platform.value}
              platform={platform}
              selected={selectedPlatforms.includes(platform.value)}
              onClick={() => handlePlatformToggle(platform.value)}
            />
          ))}
        </Box>
      </Box>

      <PageAlert error={error} />

      {errors.platforms && (
        <PageAlert error={errors.platforms} severity="error" sx={{ mb: 4 }} />
      )}

      {aiError && (
        <PageAlert
          error={aiError}
          severity="warning"
          sx={{ mb: 4 }}
          onClose={() => setAiError(null)}
        />
      )}

      <Card
        elevation={0}
        sx={{
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: 5,
          overflow: 'hidden',
          boxShadow: '0 20px 50px -20px rgba(0,0,0,0.05)',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: { xs: 3, md: 5 } }}>
            {selectedPlatforms.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Avatar sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', width: 80, height: 80, mx: 'auto', mb: 3 }}>
                  <SendIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                  Ready to go viral?
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Select one or more platforms above to start crafting your post.
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {selectedPlatforms.map((platform) => (
                    <PlatformCustomizationPanel
                      key={platform}
                      platform={platform}
                      customization={platformCustomizations[platform] || {}}
                      onChange={handleCustomizationChange}
                      errors={errors}
                      showErrors={hasSubmitted}
                      expanded={expandedPlatform === platform}
                      onExpandToggle={() =>
                        setExpandedPlatform(expandedPlatform === platform ? null : platform)
                      }
                      hasError={hasSubmitted && platformsWithErrors.includes(platform)}
                      onAskAI={() => handleAskAI(platform)}
                    />
                  ))}
                </Box>

                <SuggestionPanel
                  open={suggestionPanelOpen}
                  onClose={() => setSuggestionPanelOpen(false)}
                  loading={suggestionLoading}
                  suggestions={suggestions}
                  onApply={handleApplySuggestion}
                  platform={suggestionContext?.platform}
                />

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    data-testid="submit-post-button"
                    startIcon={<SendIcon />}
                    size="large"
                    sx={{
                      borderRadius: 4,
                      px: 8,
                      py: 2,
                      fontWeight: 900,
                      fontSize: '1.1rem',
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      boxShadow: '0 8px 25px -5px rgba(99, 102, 241, 0.5)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        boxShadow: '0 12px 30px -5px rgba(99, 102, 241, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {selectedPlatforms.some(p => platformCustomizations[p]?.isScheduled) ? 'Schedule' : 'Launch'} Post to {selectedPlatforms.length} Channel{selectedPlatforms.length > 1 ? 's' : ''}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      <PostingProgressModal
        open={modalOpen}
        platforms={postingPlatforms}
        postingStatus={postingStatus}
        results={postingResults}
        retryingPlatform={retryingPlatform}
        onRetry={handleRetry}
        onClose={() => setModalOpen(false)}
        allDone={allDone}
      />
    </Box >
  );
});

export default PostContent;
